import { useState } from "react";

import { toErrorMessage } from "../../../../components/Input/InstrumentSearch";
import { notify } from "../../../../components/Notification";
import { AdminInstrument } from "../../../../services/admin";
import {
  useRepairInstrumentListingMutation,
  useSetInstrumentSourceCountryMutation,
} from "../../../../services/instruments";
import {
  ManualPriceEntry,
  useManualPricingWrite,
  withFixSection,
} from "../../components/InstrumentEditor";
import { useInstrumentCatalogSession } from "../InstrumentCatalogProvider";
import { InstrumentEdit, InstrumentEdits } from "../types";

const failureOf = async (write: () => Promise<unknown>) => {
  try {
    await write();
    return null;
  } catch (err: unknown) {
    return toErrorMessage(err);
  }
};

export const useInstrumentEdits = (): InstrumentEdits => {
  const [setSourceCountry] = useSetInstrumentSourceCountryMutation();
  const [repairListing] = useRepairInstrumentListingMutation();
  const writeManualPricing = useManualPricingWrite();
  const { updateDraft, setSectionError } = useInstrumentCatalogSession();
  const [isSaving, setIsSaving] = useState(false);
  const [revision, setRevision] = useState(0);

  const resetEditor = () => setRevision((current) => current + 1);

  const save = async ({
    instrument: { id },
    section,
    message,
    write,
    onSaved,
  }: InstrumentEdit) => {
    setSectionError(id, section, null);
    setIsSaving(true);
    try {
      const failure = await write();
      if (failure) {
        setSectionError(id, section, failure);
        return;
      }
      if (section !== "automatic") {
        updateDraft(id, (current) => withFixSection(current, section, null));
      }
      onSaved?.();
      notify.success(message);
    } finally {
      setIsSaving(false);
    }
  };

  const saveCountry: InstrumentEdits["saveCountry"] = (
    instrument,
    sourceCountry,
  ) =>
    void save({
      instrument,
      section: "country",
      message: `${instrument.symbol} source country ${sourceCountry ? "saved" : "cleared"}`,
      write: () =>
        failureOf(() =>
          setSourceCountry({
            instrumentId: instrument.id,
            sourceCountry,
          }).unwrap(),
        ),
    });

  const repair: InstrumentEdits["repair"] = (instrument, yahooSymbol) =>
    void save({
      instrument,
      section: "listing",
      message: `${instrument.symbol} listing saved`,
      write: () =>
        failureOf(() =>
          repairListing({ instrumentId: instrument.id, yahooSymbol }).unwrap(),
        ),
    });

  const applyManualPricing = (
    instrument: AdminInstrument,
    entry: ManualPriceEntry,
  ) =>
    void save({
      instrument,
      section: "manual",
      message: instrument.manualPricingOnly
        ? `Price recorded for ${instrument.symbol}`
        : `${instrument.symbol} now uses manual pricing`,
      write: () => writeManualPricing(instrument, entry),
      // A recorded price changes nothing the editor's key reads, so the form has to be reset explicitly.
      onSaved: resetEditor,
    });

  const restoreAutomaticPricing = (instrument: AdminInstrument) =>
    void save({
      instrument,
      section: "automatic",
      message: `${instrument.symbol} now uses automatic pricing`,
      write: () =>
        failureOf(() =>
          repairListing({ instrumentId: instrument.id }).unwrap(),
        ),
    });

  return {
    isSaving,
    revision,
    resetEditor,
    saveCountry,
    repair,
    applyManualPricing,
    restoreAutomaticPricing,
  };
};
